import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import {
  buildNavigationUrl,
  createRedirectionUrlFromNavigationOptions,
  navigateByPageUrl as navigateByPageUrlUtil,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import {
  NavigationOptionEnum,
  NavigationOptionsType,
  PageNavigationMetaData,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { KeycloakWrapperService } from '../keycloak-wrapper-service/keycloak-wrapper.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(
    private configService: ConfigService,
    private store: Store,
    private keycloakService: KeycloakWrapperService,
    private router: Router,
  ) {}

  public navigateByPageUrl = ({
    url,
    targetRole,
    currentRole,
    zone,
    externalLink,
    browserBehaviour,
    navigationExtras,
  }: {
    url: string | undefined;
    targetRole: string | undefined;
    currentRole: string | undefined;
    zone: string | undefined;
    externalLink?: string | null | undefined;
    browserBehaviour?: string | null | undefined;
    navigationExtras?: NavigationExtras;
  }): Promise<boolean> => {
    return navigateByPageUrlUtil({
      url,
      targetRole,
      currentRole,
      zone,
      externalLink,
      browserBehaviour,
      navigationExtras,
      store: this.store,
      keycloakService: this.keycloakService,
      router: this.router,
    });
  };

  /**
   * Builds a navigation URL based on the provided configuration. If a pageId is included in the config, it attempts to resolve it to page metadata and constructs the URL accordingly. If no pageId is provided or if resolution fails, it falls back to creating a URL directly from the navigation options.
   * @param config - The navigation options which may include pageId, zone, role, etc.
   * @param isLoggedOutZone - A flag indicating if the navigation is for a logged out zone, which may affect URL construction
   * @returns An object containing the constructed page URL and any resolved page navigation metadata
   */
  public buildPageNavigationUrl(
    config: NavigationOptionsType,
    isLoggedOutZone = false,
  ): {
    pageUrl: string;
    pageNavigationMetadata: PageNavigationMetaData | undefined;
  } {
    // If pageId is provided, resolve it to metadata
    if (config.pageId) {
      const pageNavigationMetadata = this.configService.getPageNavigationMetadata(config.pageId);
      if (pageNavigationMetadata) {
        const resolvedConfig: NavigationOptionsType = {
          zone: pageNavigationMetadata.zone || '',
          pageUrl: pageNavigationMetadata.pageUrl,
          role: pageNavigationMetadata.persona || '',
          navigationOption: config.navigationOption || NavigationOptionEnum.USE_AS_NAVIGATION,
          pageId: config.pageId,
          tabRef: config.tabRef || '',
          pageTransition: config.pageTransition || 'forward',
          browserBehaviour: config.browserBehaviour,
          externalUrl: config.externalUrl,
        };
        return { pageUrl: buildNavigationUrl(resolvedConfig, isLoggedOutZone), pageNavigationMetadata };
      }
    }

    // Fallback to old logic - Use the utility function for URL creation
    return {
      pageUrl: createRedirectionUrlFromNavigationOptions(config, isLoggedOutZone),
      pageNavigationMetadata: undefined,
    };
  }
}
